import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { findOwningAgentId } from "./ownership.js";
import { useOwningAgentProfile } from "./useOwningAgentProfile.js";
import { createEmployerInviteCard, listEmployerInviteCards, revokeEmployerInviteCard } from "./employerInviteCardsApi.js";
import { listEmployerApplications } from "./employerApplicationsApi.js";
import CopyLinkField from "./CopyLinkField.jsx";
import {
  LINK_HIDDEN_FOR_ROLE_TEXT,
  STATUS_FILTERS,
  formatDateTh,
  usageNote,
  formatUsage,
  MetricCard,
  PublicUrlCell,
  StatusCell,
  STATUS_BADGE_VARIANT,
} from "./inviteCardShared.jsx";
import "./agentInviteCards.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Employer Invite Card"];
const DRAWER_TITLE_ID = "employer-invite-drawer-title";

// P157-R2: which language the Agent writes the letter in. An Employer Invite is
// sent to a Thailand-registered company (Thai legal name + Tax Id), so the real
// audience reads Thai or English - the same two languages the public
// /employer-register page is translated into. This is the employer counterpart
// of the worker card's LANGUAGE_OPTIONS, deliberately scoped to the languages a
// company actually reads rather than the migrant-worker language set.
const EMPLOYER_LANGUAGE_OPTIONS = [
  { value: "Thai", label: "ไทย (Thai)" },
  { value: "English", label: "English" },
];

// Legacy/blank values still read as a clean label in the table and inspector.
function formatEmployerLanguage(code) {
  const raw = String(code ?? "").trim().toLowerCase();
  if (!raw) {
    return null;
  }
  if (raw === "th" || raw.startsWith("thai") || raw.startsWith("ไทย")) {
    return "ไทย (Thai)";
  }
  if (raw === "en" || raw.startsWith("eng")) {
    return "English";
  }
  return String(code).trim();
}

/**
 * P156. The job-side Agent's Invite Card screen.
 *
 * Owner: "มันต้อง invitecard ได้เหมือนฝั่งแรงงานนะครับ แต่เป็นฝั่งนายจ้างคือให้
 * นายจ้างกรอกเองครับ เมื่อ submit จะเข้ามาในผังเลยครับ" - and then, on the first
 * plain-table attempt: "ทำ UI หน้านี้ให้เหมือนแรงงานสิครับ".
 *
 * So this is deliberately the SAME screen as AgentInviteCardsPage - the same
 * toolbar/header/metrics/policy/table/pipeline layout, the same wx-ic-* styles,
 * and the same status/usage/expiry helpers (imported from inviteCardShared,
 * not re-implemented). What differs is only what the card is FOR: the company
 * fills the form in itself at /employer-register, and the submission lands on
 * this Agent's Org Chart as pending.
 *
 * Backend is P139A's, whose tables were never created until P156 applied the
 * migration; this is the first screen over it.
 */
export default function AgentEmployerInviteCardsPage() {
  const { status: identityStatus, data: identity, error: identityError } = useBackofficeIdentity();
  const agentId = identityStatus === "ready" ? findOwningAgentId(identity?.accessScopes) : null;
  const profileState = useOwningAgentProfile(agentId);
  const profile = profileState.status === "ready" ? profileState.data : null;

  const [listState, setListState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const [drawer, setDrawer] = useState(null);
  const [applications, setApplications] = useState({ status: "loading", totalCount: 0 });

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);
  const closeDrawer = useCallback(() => setDrawer(null), []);

  useEffect(() => {
    if (!agentId) {
      return undefined;
    }

    let cancelled = false;
    setListState((previous) => ({ status: "loading", data: previous.data, error: null }));
    listEmployerInviteCards(agentId, { page: 1, pageSize: 50 })
      .then((data) => {
        if (!cancelled) {
          setListState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setListState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, reloadToken]);

  // The job side's equivalent of the worker page's QUICK REGISTER metric: how
  // many companies have actually filled the form in. A real, traceable count,
  // never a placeholder.
  useEffect(() => {
    if (!agentId) {
      return undefined;
    }

    let cancelled = false;
    listEmployerApplications(agentId, { page: 1, pageSize: 1 })
      .then((data) => {
        if (!cancelled) {
          setApplications({ status: "ready", totalCount: data?.totalCount ?? 0 });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApplications({ status: "error", totalCount: 0 });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, reloadToken]);

  // A revoked card is history, not something the Agent can still act on -
  // same rule the worker list applies.
  const items = useMemo(
    () => (listState.data?.items ?? []).filter((card) => card.status !== "Revoked"),
    [listState.data]
  );

  const filteredItems = useMemo(
    () => items.filter((card) => statusFilter === "All" || card.status === statusFilter),
    [items, statusFilter]
  );

  const metrics = useMemo(
    () => ({
      active: items.filter((card) => card.status === "Active").length,
      expired: items.filter((card) => card.status === "Expired").length,
    }),
    [items]
  );

  function handleCreated() {
    refresh();
  }

  function handleRevoked() {
    closeDrawer();
    refresh();
  }

  if (identityStatus === "loading") {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="cards" label="กำลังโหลดข้อมูลผู้ใช้งาน..." />
      </AppShell>
    );
  }

  if (identityStatus === "error") {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดข้อมูลผู้ใช้งานได้</h1>
          <p className="wx-state-body">
            {identityError?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง"}
          </p>
        </div>
      </AppShell>
    );
  }

  if (!agentId) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">หน้านี้ใช้ได้เฉพาะ Agent เจ้าของสายงาน</h1>
          <p className="wx-state-body">บัญชีของคุณไม่ได้ผูกกับ Agent ใด จึงไม่สามารถสร้างลิงก์คำเชิญได้</p>
        </div>
      </AppShell>
    );
  }

  if (listState.status === "loading" && !listState.data) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="cards" label="กำลังโหลดรายการ Invite Card..." />
      </AppShell>
    );
  }

  if (listState.status === "error" && !listState.data) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการ Invite Card ได้</h1>
          <p className="wx-state-body">กรุณาลองรีเฟรชหน้านี้อีกครั้ง</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-ic-page">
        <div className="wx-ic-head">
          <div className="wx-ic-title">
            <h1>Invite Card นายจ้าง</h1>
            <p>
              สร้างลิงก์ให้บริษัทนายจ้างเปิดผ่านเว็บและกรอกข้อมูลเอง เมื่อกรอกเสร็จจะขึ้นในผังสายงานของคุณทันที
              โดยยังไม่ถือเป็นนายจ้างในระบบจนกว่าคุณจะตรวจสอบ
            </p>
          </div>
          <div className="wx-ic-head-actions">
            <span className="wx-ic-scope-pill">
              Scope <b>{profile?.agentCode ?? "..."}</b>
              {profile?.displayName ? ` · ${profile.displayName}` : ""}
            </span>
            <button
              type="button"
              className="wx-button wx-button--primary wx-ic-create-trigger"
              onClick={() => setDrawer({ mode: "create" })}
            >
              สร้าง Invite Card
            </button>
          </div>
        </div>

        <div className="wx-ic-metrics">
          <MetricCard label="ACTIVE INVITES" value={metrics.active} note="ยังส่งต่อให้นายจ้างได้" variant="green" />
          <MetricCard
            label="รอตรวจสอบ"
            value={applications.status === "ready" ? applications.totalCount : applications.status === "error" ? "—" : "..."}
            note="นายจ้างที่กรอกฟอร์มจากลิงก์ของคุณแล้ว"
            to="/backoffice/agent/org-chart"
          />
          <MetricCard label="EXPIRED" value={metrics.expired} note="หมดอายุแล้ว ไม่สามารถใช้งานได้" />
        </div>

        <div className="wx-ic-policy">
          <div>
            <strong>{LINK_HIDDEN_FOR_ROLE_TEXT}</strong>
            <span>SuperAdmin, Super Agent และ PLC ดูสถานะได้ แต่จะไม่เห็นรหัสหรือลิงก์คำเชิญของ Agent</span>
          </div>
          <span className="wx-badge wx-badge--success">Agent-only link</span>
        </div>

        <section className="wx-panel wx-ic-list-panel">
          <div className="wx-ic-list-head">
            <h2 className="wx-panel-title">รายการ Invite Card</h2>
            <div className="wx-ic-filters">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`wx-ic-chip${statusFilter === filter ? " wx-ic-chip--active" : ""}`}
                  onClick={() => setStatusFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {items.length === 0 ? (
            <p className="wx-empty-list">ยังไม่มี Invite Card ที่สร้างโดย Agent นี้</p>
          ) : filteredItems.length === 0 ? (
            <p className="wx-empty-list">ไม่พบ Invite Card ที่ตรงกับตัวกรอง</p>
          ) : (
            <div className="wx-ic-table-wrap">
              <table className="wx-ic-table">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Invite</th>
                    <th style={{ width: "22%" }}>ข้อความถึงนายจ้าง</th>
                    <th style={{ width: "23%" }}>Public URL</th>
                    <th style={{ width: "13%" }}>Usage</th>
                    <th style={{ width: "14%" }}>Status</th>
                    <th style={{ width: "8%" }} aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((card) => (
                    <tr
                      key={card.employerInviteCardId}
                      className="wx-ic-row"
                      onClick={() => setDrawer({ mode: "view", card })}
                    >
                      <td>
                        <span className="wx-ic-cell-strong">{card.inviteCodeMasked}</span>
                        <span className="wx-ic-cell-sub">สร้างเมื่อ {formatDateTh(card.createdAtUtc) ?? "-"}</span>
                      </td>
                      <td>
                        <span className="wx-ic-cell-sub">{card.noteToEmployer || "-"}</span>
                      </td>
                      <td>
                        <PublicUrlCell card={card} />
                      </td>
                      <td>
                        <span className="wx-ic-cell-strong">{formatUsage(card)}</span>
                        <span className="wx-ic-cell-sub">{usageNote(card)}</span>
                      </td>
                      <td>
                        <StatusCell card={card} />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="wx-ic-row-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDrawer({ mode: "view", card });
                          }}
                        >
                          เปิด
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="wx-ic-pipeline">
            <div className="wx-ic-stage wx-ic-stage--active">
              <strong>1. Invite created</strong>
              <span>Agent ส่งลิงก์เว็บให้นายจ้าง</span>
            </div>
            <div className="wx-ic-stage">
              <strong>2. นายจ้างกรอกข้อมูล</strong>
              <span>บริษัทกรอกข้อมูลเองผ่านเว็บ ยังไม่ถือเป็นนายจ้างในระบบ</span>
            </div>
            <div className="wx-ic-stage">
              <strong>3. รอตรวจสอบ</strong>
              <span>ขึ้นในผังสายงานของคุณทันที รอ Agent ตรวจสอบข้อมูล</span>
            </div>
            <div className="wx-ic-stage">
              <strong>4. เป็นนายจ้างในระบบ</strong>
              <span>เปิดรับงานและระบุจำนวนแรงงานที่ต้องการได้</span>
            </div>
          </div>
        </section>
      </div>

      <DetailDrawer
        open={drawer !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-ic-row, .wx-ic-create-trigger"
      >
        {drawer?.mode === "create" && (
          <CreateEmployerInviteCardForm
            agentId={agentId}
            onCreated={handleCreated}
            onClose={closeDrawer}
            titleId={DRAWER_TITLE_ID}
          />
        )}
        {drawer?.mode === "view" && (
          <EmployerInviteCardInspector
            agentId={agentId}
            card={drawer.card}
            onRevoked={handleRevoked}
            titleId={DRAWER_TITLE_ID}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function describeCreateError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้าง Invite Card สำหรับ Agent นี้";
  }
  if (error?.status === 400) {
    return error.message?.replace(/^Request failed with status \d+$/, "") || "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  if (error?.status === 500) {
    return "ไม่สามารถสร้างหรือแสดงลิงก์ Invite Card ได้ในขณะนี้เนื่องจากปัญหาการตั้งค่าระบบ กรุณาติดต่อฝ่ายปฏิบัติการ";
  }
  return "ไม่สามารถสร้าง Invite Card ได้ กรุณาลองใหม่อีกครั้ง";
}

function CreateEmployerInviteCardForm({ agentId, onCreated, onClose, titleId }) {
  const [noteToEmployer, setNoteToEmployer] = useState("");
  const [languageCode, setLanguageCode] = useState(EMPLOYER_LANGUAGE_OPTIONS[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [createdCard, setCreatedCard] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const card = await createEmployerInviteCard(agentId, {
        noteToEmployer: noteToEmployer.trim() || undefined,
        intendedLanguageCode: languageCode || undefined,
      });
      // The full code and URL come back exactly once, on create - the list
      // only ever carries a masked code. Hold it so the Agent can copy the
      // link before closing the drawer.
      setCreatedCard(card);
      onCreated();
    } catch (caught) {
      setErrorMessage(describeCreateError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (createdCard) {
    return (
      <div>
        <span className="wx-ic-drawer-kicker">CREATE DRAWER</span>
        <h2 id={titleId} className="wx-ic-drawer-title">
          สร้าง Invite Card สำเร็จ
        </h2>
        <p className="wx-ic-drawer-desc">
          ส่งลิงก์นี้ให้บริษัทนายจ้างเปิดผ่านเว็บและกรอกข้อมูลเอง เมื่อกรอกเสร็จจะขึ้นในผังสายงานของคุณทันที
          โดยยังไม่ถือเป็นนายจ้างในระบบจนกว่าคุณจะตรวจสอบ
        </p>
        <div className="wx-ic-preview">
          <strong>Invite URL</strong>
          {createdCard.canViewInviteLink && createdCard.inviteUrl ? (
            <CopyLinkField url={createdCard.inviteUrl} />
          ) : (
            <span className="wx-ic-preview-url">{LINK_HIDDEN_FOR_ROLE_TEXT}</span>
          )}
        </div>
        <p className="wx-ic-field-hint">
          ลิงก์เต็มแสดงเพียงครั้งเดียวตอนสร้าง หลังจากปิดหน้าต่างนี้จะเห็นเฉพาะรหัสแบบย่อ
        </p>
        <div className="wx-ic-drawer-foot">
          <button type="button" className="wx-button" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <span className="wx-ic-drawer-kicker">CREATE DRAWER</span>
      <h2 id={titleId} className="wx-ic-drawer-title">
        สร้าง Invite Card นายจ้าง
      </h2>
      <p className="wx-ic-drawer-desc">
        บริษัทจะกรอกข้อมูลของตัวเองจากลิงก์นี้ คุณไม่ต้องกรอกแทน
      </p>

      <div className="wx-ic-form-row wx-ic-form-row--single">
        <div className="wx-ic-field">
          <label htmlFor="employer-invite-language">ภาษาของจดหมาย</label>
          <select
            id="employer-invite-language"
            value={languageCode}
            onChange={(event) => setLanguageCode(event.target.value)}
          >
            {EMPLOYER_LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="wx-ic-field-hint">
            หน้าลงทะเบียนของนายจ้างจะเปิดด้วยภาษานี้ก่อน และนายจ้างยังสลับภาษาเองได้
          </span>
        </div>
      </div>

      <div className="wx-ic-form-row wx-ic-form-row--single">
        <div className="wx-ic-field">
          <label htmlFor="employer-invite-note">ข้อความถึงนายจ้าง (ไม่บังคับ)</label>
          <textarea
            id="employer-invite-note"
            value={noteToEmployer}
            onChange={(event) => setNoteToEmployer(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder="เช่น กรอกข้อมูลบริษัทเพื่อเริ่มใช้งานระบบ"
          />
          <span className="wx-ic-field-hint">ข้อความนี้จะแสดงในจดหมายเชิญที่นายจ้างเปิดอ่าน</span>
        </div>
      </div>

      {errorMessage && (
        <p className="wx-ic-form-message" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="wx-ic-drawer-foot">
        <button type="button" className="wx-button" onClick={onClose}>
          ยกเลิก
        </button>
        <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
          {submitting ? "กำลังสร้าง..." : "สร้างและคัดลอกลิงก์"}
        </button>
      </div>
    </form>
  );
}

function EmployerInviteCardInspector({ agentId, card, onRevoked, titleId }) {
  const [revoking, setRevoking] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const variant = STATUS_BADGE_VARIANT[card.status] ?? "neutral";

  async function handleRevoke() {
    setRevoking(true);
    setErrorMessage(null);
    try {
      await revokeEmployerInviteCard(agentId, card.employerInviteCardId);
      onRevoked();
    } catch (caught) {
      setErrorMessage(caught?.message ?? "ไม่สามารถเพิกถอนลิงก์นี้ได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setRevoking(false);
    }
  }

  return (
    <div>
      <div className="wx-ic-inspector-head">
        <div>
          <span className={`wx-badge wx-badge--${variant}`}>{card.status}</span>
          <h2 id={titleId} className="wx-ic-drawer-title">
            Invite Card นายจ้าง
          </h2>
          <div className="wx-ic-inspector-code">{card.inviteCodeMasked}</div>
        </div>
      </div>

      {card.canViewInviteLink ? (
        card.status === "Revoked" ? (
          <p className="wx-ic-inspector-note">ลิงก์นี้ถูกเพิกถอนแล้ว ไม่สามารถใช้งานได้อีก แต่ยังเก็บไว้เป็น audit history</p>
        ) : card.inviteUrl ? (
          <div className="wx-ic-preview">
            <strong>Invite URL</strong>
            <CopyLinkField url={card.inviteUrl} />
          </div>
        ) : (
          // Same rule the worker card follows: the full URL is only ever
          // returned on create, never re-shown from the list.
          <p className="wx-ic-inspector-note">ลิงก์เต็มแสดงเฉพาะตอนสร้างเท่านั้น หากต้องการส่งใหม่ ให้สร้างลิงก์ใหม่</p>
        )
      ) : (
        <p className="wx-ic-inspector-note">{LINK_HIDDEN_FOR_ROLE_TEXT}</p>
      )}

      <div className="wx-kv-grid wx-ic-inspector-kv">
        <div>
          <div className="wx-kv-label">การใช้งาน</div>
          <div className="wx-kv-value">{formatUsage(card)}</div>
        </div>
        <div>
          <div className="wx-kv-label">หมดอายุ</div>
          <div className="wx-kv-value">{formatDateTh(card.expiresAtUtc) ?? "ไม่มีวันหมดอายุ"}</div>
        </div>
        <div>
          <div className="wx-kv-label">สร้างเมื่อ</div>
          <div className="wx-kv-value">{formatDateTh(card.createdAtUtc) ?? "-"}</div>
        </div>
        {card.status === "Revoked" && (
          <div>
            <div className="wx-kv-label">เพิกถอนเมื่อ</div>
            <div className="wx-kv-value">{formatDateTh(card.revokedAtUtc) ?? "-"}</div>
          </div>
        )}
        <div>
          <div className="wx-kv-label">ภาษาของจดหมาย</div>
          <div className="wx-kv-value">{formatEmployerLanguage(card.intendedLanguageCode) || "-"}</div>
        </div>
        <div>
          <div className="wx-kv-label">ข้อความถึงนายจ้าง</div>
          <div className="wx-kv-value">{card.noteToEmployer || "-"}</div>
        </div>
      </div>

      {errorMessage && (
        <p className="wx-ic-form-message" role="alert">
          {errorMessage}
        </p>
      )}

      {card.canViewInviteLink && card.status !== "Revoked" && (
        <div className="wx-ic-inspector-actions">
          <button type="button" className="wx-button wx-button--ghost" onClick={handleRevoke} disabled={revoking}>
            {revoking ? "กำลังเพิกถอน..." : "เพิกถอน Invite Card"}
          </button>
          <p className="wx-ic-inspector-revoke-note">
            เพิกถอนลิงก์ใหม่เท่านั้น ข้อมูลบริษัทที่ส่งมาแล้วจะยังอยู่ในผังสายงาน
          </p>
        </div>
      )}
    </div>
  );
}
